import moment from "moment";
import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { getBlogDetails } from "../../store/actions/councilActions/blogAction";
import BreadCrumb from "../BreadCrumb";
import "./blog.css";

/* One blog post, with a few more from the same category underneath. */
/*
  A YouTube watch link cannot be put in an iframe as it is; only the /embed/
  form can. Anything that is not recognised is left alone and shown as a
  plain link rather than a blank box.
*/
const embedUrl = (url) => {
  if (!url) return "";
  const match = String(url).match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/
  );
  return match ? `https://www.youtube.com/embed/${match[1]}` : "";
};

const dmy = (value) => (value ? moment(value).format("DD MMM YYYY") : "");

const BlogDetails = () => {
  const dispatch = useDispatch();
  // URL is either /blog/:ref or /blog/:frequency/:slug - the slug (or ref) resolves the post.
  const { ref, slug } = useParams();
  const key = slug || ref;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    dispatch(getBlogDetails(key)).then((res) => {
      setData(res || null);
      setLoading(false);
    });
  }, [dispatch, key]);

  const post = data?.blog;
  const related = data?.related || [];

  const breadcrumbs = [
    { path: "/", label: "Home" },
    { path: "/blogs", label: "Blogs" },
    ...(post?.category
      ? [{ path: `/blogs/${post.category.slug}`, label: post.category.name }]
      : []),
    { label: post?.title || "Blog" },
  ];

  return (
    <div class="lists-box">
      <div class="container">
        <BreadCrumb crumbs={breadcrumbs} />

        {loading ? (
          <div class="text-center py-5">
            <Spinner animation="border" role="status" size="lg">
              <span class="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : !post ? (
          <div class="py-5">
            <h4>This blog is no longer available.</h4>
            <Link class="btn btn-comman mt-3" to="/blogs">
              Back to all blogs
            </Link>
          </div>
        ) : (
          <>
            <div class="title-box blog-title-box">
              <h4>{post.title}</h4>
            </div>

            <div class="blog-meta mb-3">
              {post.category?.name && (
                <Link class="blog-chip" to={`/blogs/${post.category.slug}`}>
                  {post.category.name}
                </Link>
              )}
              <span class="ml-2">
                {dmy(post.published_at)}
                {post.author ? ` · ${post.author}` : ""}
                {` · ${Number(post.views) || 0} views`}
              </span>
            </div>

            {embedUrl(post.video_url) ? (
              <div class="blog-video">
                <iframe
                  src={embedUrl(post.video_url)}
                  title={post.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            ) : post.video_url ? (
              <p>
                <a href={post.video_url} target="_blank" rel="noreferrer">
                  Watch the video →
                </a>
              </p>
            ) : null}

            {post.file && (
              <figure class="blog-hero">
                <img src={post.file} alt={post.title} />
              </figure>
            )}

            {post.short_description && (
              <p class="blog-lead">{post.short_description}</p>
            )}

            {/* written by the council in the admin editor */}
            <div
              class="blog-body"
              dangerouslySetInnerHTML={{ __html: post.description }}
            />

            {/* a reader who got this far usually wants to ask something */}
            <div class="blog-cta">
              <span>Have a question about this?</span>
              <Link class="blog-contact" to="/contact-us">
                Contact Us
              </Link>
            </div>

            {related.length > 0 && (
              <>
                <div class="title-box mt-5">
                  <h4>More like this</h4>
                </div>
                <div class="row">
                  {related.map((r) => (
                    <div class="col-md-3 mb-4" key={r.id}>
                      <div class="blog-card">
                        <Link to={`/blog/${r.frequency || "post"}/${r.slug || r.id}`}>
                          <div class="blog-card-img">
                            <img
                              src={r.file || "/images/logo.png"}
                              alt={r.title}
                            />
                          </div>
                        </Link>
                        <div class="blog-card-body">
                          <h5>
                            <Link to={`/blog/${r.frequency || "post"}/${r.slug || r.id}`}>{r.title}</Link>
                          </h5>
                          <div class="blog-meta">{dmy(r.published_at)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BlogDetails;
